// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Modal, Dropdown, StatusBanner } from '@loa/ui-components';
import { tagsApi } from '../../api/tagsApi';
import type { MergeTagResultDTO, TagDTO } from '../../types/tag';

export interface TagMergeModalProps {
  open: boolean;
  /** The tag being merged away (source — deleted after re-pointing assignments). */
  sourceTag: TagDTO | null;
  /** All other non-archived tags — merge target candidates. */
  candidates: TagDTO[];
  onClose: () => void;
  onSuccess: (result: MergeTagResultDTO) => void;
}

/**
 * Merge modal (Tags PRD §9 "merge modal (source -> target picker + moved-count
 * confirm)"). Confirmation shows the pre-flight usage count via
 * `tagsApi.usage`, then the actual moved/collapsed counts once the merge
 * completes.
 */
export function TagMergeModal({ open, sourceTag, candidates, onClose, onSuccess }: Readonly<TagMergeModalProps>) {
  const [targetTagId, setTargetTagId] = useState('');
  const [usageCount, setUsageCount] = useState<number | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !sourceTag) return;
    setTargetTagId('');
    setError(null);
    setLoadingUsage(true);
    tagsApi.usage(sourceTag.id)
      .then((usage) => setUsageCount(usage.totalCount))
      .catch(() => setUsageCount(null))
      .finally(() => setLoadingUsage(false));
  }, [open, sourceTag]);

  const handleClose = () => {
    if (merging) return;
    onClose();
  };

  const handleMerge = async () => {
    if (!sourceTag || !targetTagId) {
      setError('Select a tag to merge into.');
      return;
    }
    setMerging(true);
    setError(null);
    try {
      const result = await tagsApi.merge(sourceTag.id, targetTagId);
      onSuccess(result);
      onClose();
    } catch (err) {
      const apiError = err as { response?: { data?: { message?: string } } };
      setError(apiError.response?.data?.message || 'Failed to merge tags. Please try again.');
    } finally {
      setMerging(false);
    }
  };

  if (!sourceTag) return null;

  return (
    <Modal open={open} onClose={handleClose} size="small">
      <Modal.Header icon="exchange" title={`Merge "${sourceTag.name}"`} onClose={handleClose} />
      <Modal.Content>
        {error && (
          <StatusBanner variant="error" message={error} visible position="inline" dismissible={false} />
        )}
        <div className="flex flex-col gap-[16px]">
          <p className="text-[14px] text-[#525252]">
            Every assignment of <strong>{sourceTag.name}</strong>
            {usageCount != null && !loadingUsage ? ` (${usageCount} assignment${usageCount === 1 ? '' : 's'})` : ''}
            {' '}will be moved onto the target tag, then <strong>{sourceTag.name}</strong> is deleted.
          </p>
          <Dropdown
            label="Merge into"
            placeholder="Select target tag"
            value={targetTagId}
            onChange={setTargetTagId}
            options={candidates
              .filter((t) => t.id !== sourceTag.id)
              .map((t) => ({ value: t.id, text: t.name }))}
            search
          />
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: merging }}
        primaryAction={{
          label: 'Merge tags',
          onClick: () => { void handleMerge(); },
          loading: merging,
          disabled: merging || !targetTagId,
        }}
      />
    </Modal>
  );
}
